import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Badge,
  Box,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
  Divider,
  CircularProgress,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { useTheme } from '@mui/material/styles';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import axios from '../../config/axiosInstance';

const POLL_INTERVAL_MS = 60000;

function NotificationBell({ refreshSignal }) {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const pollRef = useRef(null);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await axios.get('/notifications/unread_count');
      setUnreadCount(res.data?.unread_count || 0);
    } catch {
      // silencioso: no interrumpir la navegación por un fallo de polling
    }
  }, []);

  useEffect(() => {
    fetchUnreadCount();
    pollRef.current = setInterval(fetchUnreadCount, POLL_INTERVAL_MS);
    return () => clearInterval(pollRef.current);
  }, [fetchUnreadCount]);

  useEffect(() => {
    if (refreshSignal) {
      fetchUnreadCount();
    }
  }, [refreshSignal, fetchUnreadCount]);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/notifications');
      setItems(res.data?.items || []);
    } catch {
      setItems([]);
    }
    setLoading(false);
  };

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
    fetchItems();
  };

  const handleClose = () => setAnchorEl(null);

  const handleItemClick = async (item) => {
    if (item.read) return;
    setItems((prev) => prev.map((n) => (n.id === item.id ? { ...n, read: true } : n)));
    setUnreadCount((prev) => Math.max(0, prev - 1));
    try {
      await axios.post(`/notifications/${item.id}/read`);
    } catch {
      // si falla, el próximo polling de unread_count corrige el contador
    }
  };

  const formatDate = (iso) => {
    try {
      return formatDistanceToNow(parseISO(iso), { addSuffix: true, locale: es });
    } catch {
      return '';
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <IconButton onClick={handleOpen} sx={{ color: theme.palette.secondary.main }}>
        <Badge badgeContent={unreadCount} color="error" max={99}>
          <NotificationsIcon />
        </Badge>
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 360, maxHeight: 420 } }}
      >
        <Typography sx={{ px: 2, py: 1, fontWeight: 700 }}>Notificaciones</Typography>
        <Divider />
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        {!loading && items.length === 0 && (
          <MenuItem disabled sx={{ whiteSpace: 'normal' }}>
            No tenés notificaciones.
          </MenuItem>
        )}
        {!loading && items.map((item) => (
          <MenuItem
            key={item.id}
            onClick={() => handleItemClick(item)}
            sx={{
              whiteSpace: 'normal',
              alignItems: 'flex-start',
              py: 1,
              backgroundColor: item.read ? 'transparent' : 'action.hover',
            }}
          >
            <ListItemText
              primary={
                <Typography sx={{ fontWeight: item.read ? 500 : 700, fontSize: '0.9rem' }}>
                  {item.title}
                </Typography>
              }
              secondary={
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                    {item.message}
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    {formatDate(item.created_at)}
                  </Typography>
                </>
              }
            />
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}

export default NotificationBell;
